/**
 * TransferStatus — shows per-body upload and per-case submission progress.
 */

import { Badge, Box, Card, Flex, Text } from "@radix-ui/themes";
import type { TransferResult, CaseCreationResult } from "../api/geometry";

const STATUS_ICON: Record<string, { icon: string; color: string }> = {
  uploaded: { icon: "\u2713", color: "green" },
  processing: { icon: "\u23F3", color: "blue" },
  processed: { icon: "\u2713", color: "green" },
  submitted: { icon: "\u2713", color: "green" },
  pending: { icon: "\u23F3", color: "blue" },
  error: { icon: "\u2717", color: "red" },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_ICON[status] ?? { icon: "?", color: "gray" };
  return (
    <Badge size="1" variant="soft" color={s.color as never}>
      {s.icon} {status}
    </Badge>
  );
}

interface Props {
  transferResult: TransferResult | null;
  caseResults: CaseCreationResult | null;
}

export function TransferStatus({ transferResult, caseResults }: Props) {
  if (!transferResult && !caseResults) return null;

  return (
    <Box>
      {transferResult && (
        <Box mb="3">
          <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
            Geometry Upload
          </Text>
          {Object.entries(transferResult.geometries).map(([name, info]) => (
            <Card key={name} size="1" mb="1">
              <Flex align="center" justify="between">
                <Flex align="center" gap="2">
                  <Text size="2">{name}</Text>
                  <Badge size="1" variant="outline">
                    {info.role}
                  </Badge>
                </Flex>
                <StatusBadge status={info.status} />
              </Flex>
              {info.error && (
                <Text size="1" color="red" mt="1" style={{ display: "block" }}>
                  {info.error}
                </Text>
              )}
              {info.flow360_geometry_id && (
                <Text size="1" color="gray" mt="1" style={{ display: "block" }}>
                  ID: {info.flow360_geometry_id}
                </Text>
              )}
            </Card>
          ))}
        </Box>
      )}

      {caseResults && (
        <Box>
          <Text size="2" weight="medium" mb="2" style={{ display: "block" }}>
            Deflection Cases ({caseResults.cases_created} created
            {caseResults.cases_failed > 0 &&
              `, ${caseResults.cases_failed} failed`}
            )
          </Text>
          <Box
            style={{
              maxHeight: 200,
              overflow: "auto",
              borderRadius: 6,
              border: "1px solid var(--gray-5)",
            }}
          >
            {caseResults.cases.map((c, i) => (
              <Flex
                key={i}
                align="center"
                justify="between"
                px="2"
                py="1"
                style={{
                  borderBottom:
                    i < caseResults.cases.length - 1
                      ? "1px solid var(--gray-4)"
                      : undefined,
                }}
              >
                <Text size="1" style={{ fontFamily: "monospace" }}>
                  {Object.entries(c.deflections)
                    .map(([k, v]) => `${k}=${v > 0 ? "+" : ""}${v}°`)
                    .join("  ")}
                </Text>
                <StatusBadge status={c.status} />
              </Flex>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
